/* eslint-disable max-statements */
import { Grid, Stack, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useMemo } from 'react';

import { Declaration, DeclarationHighValueGroupType, EnvironmentCountries, TransactionTypes } from '@e-origin/shared';

import { AsyncAutocomplete, EucdmCode, HideElementFor, MultiLangCodes, Select, TextInput } from '@components';
import { useCustomers, useCustomersNamesAndIds } from '@hooks/customer';
import { useDeclarant, useGoodLocation } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField } from '@utils';
import { BeforeSendingFormData } from './beforeSendingBatchFormIntializer';

interface FinalizeGlobalParameterProps {
  form: UseFormReturnType<BeforeSendingFormData>;
  disabled: boolean;
  declaration?: Declaration;
  brokerId: string;
}

const MESSAGE_INFO_SECURITY_FIELD = 'messageInfo.security';

export const FinalizeGlobalParameter: React.FC<FinalizeGlobalParameterProps> = ({ form, disabled, declaration, brokerId }) => {
  const isExport = declaration?.generalInfo?.isExport;
  const declarationMainType = declaration?.messageInfo?.declarationMainType;
  const declarationGroup = declaration?.generalInfo?.group as DeclarationHighValueGroupType;
  const isH2Group = declarationGroup === DeclarationHighValueGroupType.H2;
  const { data: declarant } = useDeclarant();
  const { data: settings } = useSettings();
  const { data: { data: goodsLocations } = {} } = useGoodLocation(declarant._id);

  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  const customersNamesAndIds = useCustomersNamesAndIds(brokerId ? { filters: { brokerId: brokerId, valid: true } } : undefined);
  const { data: { list: customers } = {} } = useCustomers(brokerId ? { filters: { brokerId: brokerId, valid: true } } : undefined);
  const getCustomerValue = customersNamesAndIds.find((customer) => customer.value === form.values.customer)?.value;

  const warehouseData = useMemo(() => {
    if (!form.values.consignment.locationOfGoods.unLocode || !goodsLocations) {
      return [];
    }

    const selectedLocation = goodsLocations.find((loc) => loc.unLocode === form.values.consignment.locationOfGoods.unLocode);

    return (
      selectedLocation?.warehouses?.map((warehouse) => ({
        label: `${warehouse.type} - ${warehouse.identifier}`,
        value: warehouse.identifier || '',
      })) || []
    );
  }, [form.values.consignment.locationOfGoods.unLocode, goodsLocations]);

  const updateCustomerHandler = (selectedOption: string) => {
    form.setFieldValue('customer', selectedOption);

    const newCustomer = customers.find((customer) => customer._id === selectedOption);

    form.setFieldValue('messageInfo.methodOfPayment', newCustomer.payments.methodOfPayment);
    form.setFieldValue('messageInfo.deferredPayment', newCustomer.payments.deferredPayment);
    form.setFieldValue('messageInfo.methodOfPaymentVAT', newCustomer.payments.methodOfPaymentVAT);

    form.setTouched({ customer: true });
  };
  const hideSecurityField = !isExport && declarationMainType === 'IM';
  const isB2BTransaction = [TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT, TransactionTypes.B2B2C_HV, undefined].includes(declaration.transactionType);

  const updateWarehouseHandler = (selectedWarehouseIdentifier: string) => {
    form.setFieldValue('warehouse', selectedWarehouseIdentifier);

    const selectedLocation = goodsLocations?.find((loc) => loc.unLocode === form.values.consignment.locationOfGoods.unLocode);
    const selectedWarehouse = selectedLocation?.warehouses?.find((w) => w.identifier === selectedWarehouseIdentifier);

    if (selectedWarehouse) {
      const warehouseDoc = {
        identifier: selectedWarehouse.identifier,
        type: selectedWarehouse.type,
        header: true,
      };
      const currentDocs = form.values.documents?.warehouseDocuments || [];
      const updatedDocs = currentDocs.length ? [warehouseDoc, ...currentDocs.slice(1)] : [warehouseDoc];
      form.setFieldValue('documents.warehouseDocuments', updatedDocs);
    }
  };

  return (
    <>
      <Grid mb="sm">
        <Grid.Col span={6}>
          <EucdmCode path="messageInfo.declarationMainType">
            <MultiLangCodes
              {...form.getInputProps('messageInfo.declarationMainType')}
              label="Declaration Main Type"
              code="CL231"
              size="lg"
              disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              data-pw="declaration-main-type-input"
              system={codelistSystems}
            />
          </EucdmCode>
        </Grid.Col>
        <Grid.Col span={6}>
          <EucdmCode path="messageInfo.declarationType">
            <MultiLangCodes
              {...form.getInputProps('messageInfo.declarationType')}
              label="Declaration Type"
              code="CL042"
              disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              size="lg"
              data-pw="declaration-type-input"
              system={codelistSystems}
            />
          </EucdmCode>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <HideElementFor systems={['DMS']} declarationGroup={declarationGroup} condition={() => hideSecurityField}>
          <Grid.Col span={4}>
            <EucdmCode path={MESSAGE_INFO_SECURITY_FIELD}>
              <MultiLangCodes
                {...form.getInputProps(MESSAGE_INFO_SECURITY_FIELD)}
                label="Security"
                code="CL217"
                onChange={(selectedOption) => form.setFieldValue(MESSAGE_INFO_SECURITY_FIELD, Number(selectedOption))}
                value={form.values.messageInfo.security?.toString()}
                size="lg"
                disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.NL] })}
                data-pw="security-input"
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
        </HideElementFor>
        <Grid.Col span={4}>
          <Select
            placeholder="Select customer"
            label="Customer"
            data={customersNamesAndIds}
            value={getCustomerValue}
            onChange={updateCustomerHandler}
            size="lg"
            disabled={
              disabled ||
              disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.NL, EnvironmentCountries.BE] })
            }
            data-pw="customer-input"
          />
        </Grid.Col>
        {isB2BTransaction && (
          <Grid.Col span={4}>
            <TextInput {...form.getInputProps('customsState.bcpMrn')} label="BCP MRN" size="lg" disabled={disabled} data-pw="bcp-mrn-input" />
          </Grid.Col>
        )}
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={isH2Group ? 4 : 6}>
          <EucdmCode path="consignment.customsOfficePresentation.referenceNumber">
            <MultiLangCodes
              {...form.getInputProps('consignment.customsOfficePresentation.referenceNumber')}
              label="Customs Presentation Office"
              code={isExport ? 'CL193' : 'CL141'}
              size="lg"
              disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.NL] })}
              data-pw="customs-office-presentation-input"
              system={codelistSystems}
            />
          </EucdmCode>
        </Grid.Col>
        <Grid.Col span={isH2Group ? 4 : 6}>
          <EucdmCode path="declaration.consignment.locationOfGoods.unLocode">
            <AsyncAutocomplete
              {...form.getInputProps('consignment.locationOfGoods.unLocode')}
              label="Goods location"
              data={goodsLocations.map((location) => ({
                value: location.unLocode,
                label: location.unLocode,
              }))}
              value={form.values.consignment.locationOfGoods.unLocode}
              size="lg"
              disabled={disabled}
              data-pw="goods-location-input"
            />
          </EucdmCode>
        </Grid.Col>
        <HideElementFor condition={() => !isH2Group}>
          <Grid.Col span={4}>
            <Stack>
              <Select
                key={form.values.warehouse}
                {...form.getInputProps('warehouse')}
                label="Bonded warehouse"
                data={warehouseData}
                searchable
                size="lg"
                disabled={!form.values.consignment.locationOfGoods.unLocode || disabled}
                onChange={updateWarehouseHandler}
              />
              {form.values.warehouse && !warehouseData.some((w) => w.value === form.values.warehouse) && (
                <Text c="red" size="sm" mt={4}>
                  WARNING! The location set in documents is not in the list of bonded warehouses of location {form.values.consignment.locationOfGoods.unLocode}
                </Text>
              )}
            </Stack>
          </Grid.Col>
        </HideElementFor>
      </Grid>
      {isExport && (
        <Grid mb="sm">
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('consignment.customsOfficeExit.referenceNumber')}
              label="Customs Exit office"
              size="lg"
              disabled={disabled}
              data-pw="customs-office-exit-input"
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('consignment.customsOfficeExport.referenceNumber')}
              label="Customs Export office"
              size="lg"
              disabled={disabled}
              data-pw="customs-office-export-input"
            />
          </Grid.Col>
        </Grid>
      )}
      {!isExport && (
        <Grid mb="sm">
          <Grid.Col span={12}>
            <EucdmCode path="consignment.customsSupervisingOffice.referenceNumber">
              <MultiLangCodes
                {...form.getInputProps('consignment.customsSupervisingOffice.referenceNumber')}
                label="Supervising customs office"
                code="CL141"
                size="lg"
                disabled={
                  disabled ||
                  disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE, EnvironmentCountries.NL] })
                }
                data-pw="supervising-customs-office-input"
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      )}
    </>
  );
};
